/**
 * A daisyUI tooltip around a control that already has its own accessible name.
 *
 * The bubble is presentation only, so the control must say the same thing
 * itself; `HelpTip` is the one to reach for when the explanation is the point.
 *
 * @module components/ui/Tooltip
 */

import type { ReactNode } from "react";

export const TOOLTIP_PLACEMENTS = ["top", "bottom", "left", "right"] as const;
export type TooltipPlacement = (typeof TOOLTIP_PLACEMENTS)[number];

/** Written out, not interpolated: Tailwind only generates classes it can read. */
const ALWAYS: Record<TooltipPlacement, string> = {
  top: "tooltip tooltip-top",
  bottom: "tooltip tooltip-bottom",
  left: "tooltip tooltip-left",
  right: "tooltip tooltip-right",
};

const FROM_SM: Record<TooltipPlacement, string> = {
  top: "sm:tooltip sm:tooltip-top",
  bottom: "sm:tooltip sm:tooltip-bottom",
  left: "sm:tooltip sm:tooltip-left",
  right: "sm:tooltip sm:tooltip-right",
};

export interface TooltipProps {
  tip: string;
  placement?: TooltipPlacement;
  /**
   * Suppress the bubble below `sm`, for a control at a screen edge where a
   * phone would paint the bubble outside its card and clip it.
   */
  fromSm?: boolean;
  /** Layout for the wrapper, such as `block max-w-full` to let a child truncate. */
  className?: string;
  children: ReactNode;
}

export function Tooltip({
  tip,
  placement = "top",
  fromSm = false,
  className = "",
  children,
}: TooltipProps) {
  return (
    <span
      className={[(fromSm ? FROM_SM : ALWAYS)[placement], className].filter(Boolean).join(" ")}
      data-tip={tip}
    >
      {children}
    </span>
  );
}
